<template>
    <lsn-modal :is-open="isDialogOpen">
        <div
            class="sl-prompt w-[440px]"
            :class="{ 'sl-prompt--info': !loading }"
        >
            <div class="sl-prompt__contents">
                <div class="sl-prompt__title text-center">
                    {{ t('orders.nightflex_v8_modal.title') }}
                </div>

                <div class="sl-prompt__text text-center">
                    {{ t('orders.nightflex_v8_modal.text') }}
                </div>

                <div
                    class="grid gap-4 mt-4"
                    :class="{
                        'grid-cols-1': Object.keys(selected).length === 1,
                        'grid-cols-2': Object.keys(selected).length === 2,
                    }"
                >
                    <div v-for="(nfv8, side) in selected" :key="nfv8.reference">
                        <label class="block">
                            <div class="flex justify-between items-center gap-1 mb-1 text-sm">
                                <div>{{ t(`common.${side}-lens`) }}</div>

                                <div>{{ nfv8.serial_number }}</div>
                            </div>

                            <lsn-input
                                ref="inputs"
                                v-model="clSimId[side]"
                                placeholder="CLSIMID"
                                class="lsn-form__wrapper--padless"
                                :error="errors[side]"
                                @keydown.enter="reOrder"
                                @keydown.esc="emit('close')"
                            />
                        </label>
                    </div>
                </div>

                <div
                    v-if="mayReOrderWithOldUI.length"
                    class="cursor-pointer mt-4 text-primary-500 text-center text-sm transition
                        hover:text-primary-700 hover:underline"
                    @click="reOrderWithOldUI"
                >
                    {{ t('orders.nightflex_v8_modal.reorder_with_old_ui') }}
                </div>
            </div>

            <div class="sl-prompt__actions">
                <button
                    class="sl-prompt__button sl-prompt__button--white"
                    @click.stop="emit('close')"
                >
                    {{ t('common.cancel') }}
                </button>

                <button
                    class="sl-prompt__button"
                    :class="{
                        'sl-prompt__button--primary': !loading,
                        'sl-prompt__button--disabled': loading,
                    }"
                    :disabled="loading"
                    @click.stop="reOrder"
                >
                    {{ t('common.ok') }}
                </button>
            </div>
        </div>
    </lsn-modal>
</template>

<script setup>
import {
    nextTick,
    ref,
    shallowRef,
    useTemplateRef,
    watch,
} from 'vue';
import LensOrderUrlManager from './lenses/LensOrderUrlManager';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import axios from '@/axios';
import NightFlexVariantError from '@/errors/NightFlexVariantError';


// ------------------------------------------------------------ PROPS

const props = defineProps({
    isOpen:
    {
        type:    Boolean,
        default: false,
    },

    selected:
    {
        type:    Object,
        default: () => ({}),
    },
});


// ------------------------------------------------------------ EMITS

const emit = defineEmits(['close']);


// ------------------------------------------------------------ COMPOSABLES

const router = useRouter();
const route = useRoute();
const store = useStore();
const { t } = useI18n();


// ------------------------------------------------------------ TEMPLATE REFS

const inputs = useTemplateRef('inputs');


// ------------------------------------------------------------ DATA

const isDialogOpen = ref(props.isOpen);
const loading = ref(false);
const clSimId = ref({ right: null, left: null });
const errors = ref({ right: null, left: null });
const mayReOrderWithOldUI = ref([]);
const nfV8Params = shallowRef({});


// ------------------------------------------------------------ COMPUTED

// ...


// ------------------------------------------------------------ METHODS

async function reOrder()
{
    if(loading.value)
    {
        return;
    }

    loading.value = true;
    console.group('Re Order NightFlex v8');

    // Reset errors/state
    errors.value = { right: null, left: null };
    mayReOrderWithOldUI.value = [];
    const um = new LensOrderUrlManager(route);
    nfV8Params.value = {};

    for(const side in props.selected)
    {
        // Validate CLSIMID
        const theClSimId = (clSimId.value[side] ?? '').trim();
        if(theClSimId === '')
        {
            console.log('empty CLSIMID', { side });
            errors.value[side] = t('validation.required');
            continue;
        }
        else
        {
            console.log('valid CLSIMID', { side, clSimId: theClSimId });
        }

        // Retrieve imported NFv8 parameters for the selected CLSIMIDs
        const order = props.selected[side];
        const params = {};
        const url = `/api/nightflex-v8/${theClSimId}/parameters`;

        try
        {
            console.log('fetching params', { side, url });
            const response = await axios.get(url);
            for(const key in response.data)
            {
                params[key] = response.data[key];
            }
            console.log('received params', { side, params });
        }
        catch(e)
        {
            console.error('error fetching params', { e });

            // Set error message for HTTP errors
            switch(e.response?.status)
            {
                case 404:
                    errors.value[side] = t('validation.not_found');
                    break;

                case 500:
                    errors.value[side] = t('common.errors.error');
                    break;

                default:
                    errors.value[side] = e.message;
                    break;
            }

            // Allow reordering with the old UI
            mayReOrderWithOldUI.value.push(order);
            continue;
        }

        // Validate variant consistency
        // const orderVariant = order.article_data.LENS_VARIANT;
        const excelVariant = params.LENS_VARIANT;
        // if(orderVariant === 'HIGH_MYOPIA' && excelVariant === 'MYOPIA')
        // {
        //     // Hack: they're actually the same model in NF2025.
        //     // Do nothing.
        // }
        // else if(orderVariant !== excelVariant)
        // {
        //     // Set error message for inconsistent old/new variants
        //     errors.value[side] = t('orders.nightflex_v8_modal.errors.inconsistent_variants_colon') +
        //         ` ${orderVariant} ≠ ${excelVariant}`;
        //     continue;
        // }

        // // Check if model is supported
        // const supportedVariants = [
        //     'MYOPIA',
        //     // todo: add support for more models
        // ];
        // if(!supportedVariants.includes(excelVariant))
        // {
        //     // Set error message for unsupported variant
        //     errors.value[side] = t('orders.nightflex_v8_modal.errors.unsupported_variant_colon') +
        //         ' ' + excelVariant;
        //     continue;
        // }

        // Set order reference
        const reference = order.reference;
        um.setOrderReference(side, reference);
        console.log('set order reference', { side, reference });

        // Convert prototype from NightFlex v8 to NightFlex 2025
        const proto8 = order.article_data.PROTOTYPE_CODE;
        // const variant = order.article_data.LENS_VARIANT ??
        //     proto8.match(/^NIGHTFLEX_A?SYMMETRICAL_(\w+)$/)?.[1];
        const variant = excelVariant;

        try
        {
            const proto2025 = convertNightFlexV8PrototypeTo2025(variant);
            um.setPrototypeCode(side, proto2025);
            console.log('set proto', { side, proto8, proto2025 });
        }
        catch(e)
        {
            if(e instanceof NightFlexVariantError)
            {
                console.error(e.name, { proto8, variant, e });
                errors.value[side] = `${e.variant}: ${e.message}`;
            }
            else
            {
                console.error('could not convert prototype', { proto8, variant, e });
                errors.value[side] = t('common.errors.error');
            }
        }

        // Build template data
        nfV8Params.value[side] = {
            ...params,
            PATIENT_REFERENCE1:  order.article_data.PATIENT_REFERENCE1,
            RENEWAL_PERIOD:      order.article_data.RENEWAL_PERIOD,
            RETURN_OPTION:       order.article_data.RETURN_OPTION,
            LENS_MATERIAL:       order.article_data.LENS_MATERIAL,
            LENS_MATERIAL_COLOR: order.article_data.LENS_MATERIAL_COLOR,

            PROTOTYPE_CODE: um.getPrototypeCode(side),
            reference:      um.getOrderReference(side),
        };
        console.log('built template data', nfV8Params.value);
    }

    loading.value = false;

    // Stop on error
    if(errors.value.right || errors.value.left)
    {
        console.log('errors, aborting');
        console.groupEnd();
        return;
    }

    // Navigate to the order form.
    // The `templateData` state will be retrieved in those handlers:
    // - `SetPrototypePreviousValuesByPreviousOrder`
    // - `TemplatePrototypeForms`
    // It will then be cleared at the end of the dispatch() method of
    // LensOrder.vue.
    const path = um.getPath();
    const templateData = { ...nfV8Params.value };

    console.log('navigate to order form', { path, templateData });
    console.groupEnd();

    store.dispatch('lensOrder/setTemplateData', { ...nfV8Params.value });
    router.push({ path: um.getPath() });
}

function convertNightFlexV8PrototypeTo2025(variant)
{
    switch(variant)
    {
        // Supported variants
        case 'myopia-3r':
            // TODO: Use NF2025 (non-test) as long as the test one hasn't been published!
            // return 'NIGHTFLEX2025_MYOPIA';
            return 'NIGHTFLEX2025TEST_MYOPIA';

        case 'p-cd-3r':
            return 'NIGHTFLEX2025_PRESBYOPIA_CD';

        case 'relax-3r':
            return 'NIGHTFLEX2025_RELAX';

        // Unsupported variants
        case 'p-cn-2r':
            // return 'NIGHTFLEX2025_PRESBYOPIA_CN';
            throw new NightFlexVariantError(
                'Via Excel. Lens status unknown.',
                variant
            );

        case 'free':
            throw new NightFlexVariantError(
                'No Excel. Should be entered manually into the Liberty interface.',
                variant
            );

        case 'std-1r':
            // return 'NIGHTFLEX2025_MYOPIASTD';
            throw new NightFlexVariantError(
                'Via Excel. Will be integrated in SL6 Q1, 2026.',
                variant
            );

        case 'hyper-3r':
            // return 'NIGHTFLEX2025_HYPEROPIA';
            throw new NightFlexVariantError(
                'Via Excel. Will be integrated in SL6 Q2, 2026.',
                variant
            );

        default:
            throw new NightFlexVariantError(
                `No Excel. No Excel. Should be entered manually into SL6.`,
                variant
            );
    }
}

function reOrderWithOldUI()
{
    if(!mayReOrderWithOldUI.value.length)
    {
        return;
    }

    const um = new LensOrderUrlManager(route);
    for(const order of mayReOrderWithOldUI.value)
    {
        const side = order.article_data.LENS_SIDE;
        if(['right', 'left'].includes(side))
        {
            um.setOrderReference(side, order.reference);
        }
        else if(um.has('right'))
        {
            um.setOrderReference('left', order.reference);
        }
        else
        {
            um.setOrderReference('right', order.reference);
        }

        const proto = order.article_data.PROTOTYPE_CODE;
        um.setPrototypeCode(side, proto);
    }

    for(const side in nfV8Params.value)
    {
        const reference = nfV8Params.value[side].reference;
        const proto = nfV8Params.value[side].PROTOTYPE_CODE;
        um.setOrderReference(side, reference);
        um.setPrototypeCode(side, proto);
    }

    // Navigate to the order form
    const path = um.getPath();
    const templateData = { ...nfV8Params.value };

    console.log('navigate to order form', { path, templateData });

    store.dispatch('lensOrder/setTemplateData', templateData);
    router.push({ path });
}


// ------------------------------------------------------------ WATCHERS

watch(() => props.isOpen, newVal => isDialogOpen.value = newVal);

watch(isDialogOpen, async newVal =>
{
    // Focus the first input when the dialog opens
    if(newVal)
    {
        await nextTick();
        inputs.value?.[0]?.focus();
    }
    else
    {
        // Reset popup errors/state
        clSimId.value = { right: null, left: null };
        errors.value = { right: null, left: null };
        mayReOrderWithOldUI.value = [];
    }
});
</script>
